import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Prod01 } from '../../constants/ActionType';
import { formatDateTime } from '../../utils/dateFormat';

// Patient profile > Insurance. Org Manager enters it; Super Admin / billing may also edit; accounting reads.
// The member ID is stored encrypted and only the last 4 digits are ever shown.
const SLOTS = [['primary', 'Primary'], ['secondary', 'Secondary']];
const STATE_STYLE = {
    complete: { bg: '#e8f5e9', fg: '#1b5e20' },
    incomplete: { bg: '#fff4e0', fg: '#8a5a00' },
    expired: { bg: '#fdecea', fg: '#b71c1c' },
    missing: { bg: '#fdecea', fg: '#b71c1c' },
};
const day = (d) => (d ? moment.utc(d).format('YYYY-MM-DD') : '');
const errText = (e) => e?.response?.data?.message || e?.message || 'Request failed';
const blank = (c) => ({
    planType: c?.planType || '',
    payerId: c?.payerId || '',
    memberId: '',
    groupNumber: c?.groupNumber || '',
    effectiveDate: day(c?.effectiveDate),
    terminationDate: day(c?.terminationDate),
    verified: false,
});

export const InsuranceBadge = ({ status }) => {
    if (!status) return null;
    const s = STATE_STYLE[status.state] || STATE_STYLE.missing;
    return (
        <span data-testid="ins-status" style={{ background: s.bg, color: s.fg, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
            {status.complete ? 'Insurance complete' : `Blocked — ${status.label}`}
        </span>
    );
};

const CoverageView = ({ c }) => {
    if (!c) return <p className="text-muted mb-0" style={{ fontSize: 13 }}>Not entered.</p>;
    return (
        <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
            <tbody>
                <tr><th style={{ width: 160 }}>Plan type</th><td>{c.planType || '—'}</td></tr>
                <tr><th>Payer</th><td>{c.payerName || '—'}</td></tr>
                <tr><th>Member ID</th><td data-testid="ins-member">{c.memberId || '—'}</td></tr>
                <tr><th>Group number</th><td>{c.groupNumber || '—'}</td></tr>
                <tr><th>Effective / ends</th><td>{day(c.effectiveDate) || '—'} / {day(c.terminationDate) || 'open'}</td></tr>
                <tr><th>Verified</th><td>{c.verifiedByName ? `${c.verifiedByName} on ${day(c.verifiedDate)}` : '—'}</td></tr>
            </tbody>
        </table>
    );
};

const CoverageForm = ({ slot, form, setForm, planTypes, payers, current }) => {
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const selfPay = form.planType === 'Self-pay';
    const id = (k) => `ins-${slot}-${k}`;
    return (
        <div style={{ fontSize: 13 }}>
            <label htmlFor={id('plan')} className="mb-0">Plan type</label>
            <select id={id('plan')} className="form-control form-control-sm mb-1" value={form.planType} onChange={set('planType')}>
                <option value="">—</option>
                {planTypes.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {!selfPay && (
                <>
                    <label htmlFor={id('payer')} className="mb-0">Payer</label>
                    <select id={id('payer')} className="form-control form-control-sm mb-1" value={form.payerId} onChange={set('payerId')}>
                        <option value="">—</option>
                        {payers.map((p) => <option key={p._id} value={p._id}>{[p.name, p.plan].filter(Boolean).join(' - ')}{p.type ? ` (${p.type})` : ''}</option>)}
                    </select>
                    <label htmlFor={id('member')} className="mb-0">Member ID {current?.memberIdOnFile ? <small className="text-muted">(on file: {current.memberId}; leave blank to keep)</small> : null}</label>
                    <input id={id('member')} autoComplete="off" className="form-control form-control-sm mb-1" value={form.memberId} onChange={set('memberId')} />
                    <label htmlFor={id('group')} className="mb-0">Group number</label>
                    <input id={id('group')} className="form-control form-control-sm mb-1" value={form.groupNumber} onChange={set('groupNumber')} />
                </>
            )}
            <label htmlFor={id('eff')} className="mb-0">Effective date</label>
            <input id={id('eff')} type="date" className="form-control form-control-sm mb-1" value={form.effectiveDate} onChange={set('effectiveDate')} />
            <label htmlFor={id('term')} className="mb-0">Termination date (blank = open)</label>
            <input id={id('term')} type="date" className="form-control form-control-sm mb-1" value={form.terminationDate} onChange={set('terminationDate')} />
            <div className="form-check">
                <input id={id('verified')} type="checkbox" className="form-check-input" checked={form.verified} onChange={set('verified')} />
                <label htmlFor={id('verified')} className="form-check-label">I verified this coverage today</label>
            </div>
        </div>
    );
};

const toBody = (f, current) => {
    const b = { planType: f.planType || null, effectiveDate: f.effectiveDate || null, terminationDate: f.terminationDate || null };
    if (f.planType !== 'Self-pay') {
        b.payerId = f.payerId || null;
        b.groupNumber = f.groupNumber || null;
        if (f.memberId.trim()) b.memberId = f.memberId.trim();
        else if (!current?.memberIdOnFile) b.memberId = null;
    }
    if (f.verified) b.verified = true;
    return b;
};

const PatientInsuranceSection = ({ patientId, prefillText }) => {
    const { user } = useSelector((state) => state.auth);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [payers, setPayers] = useState([]);
    const [forms, setForms] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!patientId) return;
        setError(null);
        try { setData((await axios.get(`${Prod01}/insurance/patient/${patientId}`)).data); } catch (e) { setError(errText(e)); }
    }, [patientId]);
    useEffect(() => { load(); }, [load]);

    const startEdit = async (prefill) => {
        const primary = blank(data.primary);
        setForms({ primary, secondary: blank(data.secondary) });
        setEditing(true);
        let list = [];
        try { list = ((await axios.get(`${Prod01}/billing/payers`)).data || []).filter((p) => p.active !== false); } catch (e) { list = []; }
        setPayers(list);
        if (typeof prefill === 'string' && prefill.trim() && !primary.payerId) {
            const t = prefill.trim().toLowerCase();
            const hit = list.find((p) => (p.name || '').toLowerCase() === t) || list.find((p) => t.includes((p.name || '').toLowerCase()) && p.name) || list.find((p) => (p.name || '').toLowerCase().includes(t));
            if (hit) setForms((f) => ({ ...f, primary: { ...f.primary, payerId: hit._id } }));
        }
    };
    const [prefillUsed, setPrefillUsed] = useState(false);
    useEffect(() => {
        if (prefillText && data && !prefillUsed && !data.primary) {
            setPrefillUsed(true);
            startEdit(prefillText);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefillText, data]);
    const save = async () => {
        setError(null);
        setSaving(true);
        try {
            const body = { primary: toBody(forms.primary, data.primary) };
            if (forms.secondary.planType || data.secondary) body.secondary = forms.secondary.planType ? toBody(forms.secondary, data.secondary) : null;
            setData((await axios.post(`${Prod01}/insurance/patient/${patientId}`, body)).data);
            setEditing(false);
        } catch (e) { setError(errText(e)); }
        setSaving(false);
    };

    if (!patientId) return null;
    const canAddPayer = ['admin', 'billing'].includes(user?.role);
    return (
        <div className="card mb-3" data-testid="patient-insurance">
            <div className="card-body">
                <div className="d-flex align-items-center mb-2">
                    <h6 className="mb-0 mr-2">Insurance</h6>
                    {data && <InsuranceBadge status={data.status} />}
                    {data?.canEdit && !editing && <button type="button" className="btn btn-outline-primary btn-sm ml-auto" onClick={startEdit}>Edit insurance</button>}
                </div>
                {error && <div className="alert alert-danger py-1" role="alert">{error}</div>}
                {data && !data.status.complete && (
                    <p className="text-danger mb-2" style={{ fontSize: 13 }}>
                        Blocked — missing insurance: this patient is counted but left out of dollar forecasts, and device requests cannot be approved until coverage is complete.
                    </p>
                )}
                {data?.secondaryExpired && <p className="text-warning mb-2" style={{ fontSize: 13 }}>Secondary coverage has expired.</p>}
                {data && !editing && (
                    <div className="row">
                        {SLOTS.map(([k, label]) => (
                            <div className="col-md-6" key={k}><b style={{ fontSize: 13 }}>{label}</b><CoverageView c={data[k]} /></div>
                        ))}
                    </div>
                )}
                {data && editing && (
                    <>
                        <div className="row">
                            {SLOTS.map(([k, label]) => (
                                <div className="col-md-6" key={k}>
                                    <b style={{ fontSize: 13 }}>{label}</b>
                                    <CoverageForm slot={k} form={forms[k]} setForm={(f) => setForms({ ...forms, [k]: f })} planTypes={data.planTypes} payers={payers} current={data[k]} />
                                </div>
                            ))}
                        </div>
                        <div className="mt-2">
                            <button type="button" className="btn btn-primary btn-sm mr-1" onClick={save} disabled={saving}>Save insurance</button>
                            <button type="button" className="btn btn-light btn-sm" onClick={() => setEditing(false)}>Cancel</button>
                            {canAddPayer && <Link className="btn btn-link btn-sm" to="/billing/payers">Payer not listed? Add it on Payers</Link>}
                        </div>
                    </>
                )}
                {data?.history?.length > 0 && (
                    <details className="mt-2" style={{ fontSize: 12 }}>
                        <summary>Change history ({data.history.length})</summary>
                        <ul className="mb-0">
                            {data.history.map((h, i) => (
                                <li key={i}>{formatDateTime(h.at)} — {h.byName || 'unknown'}: {(h.changes || []).map((c) => `${c.slot} ${c.field}`).join(', ')}</li>
                            ))}
                        </ul>
                    </details>
                )}
            </div>
        </div>
    );
};

export default PatientInsuranceSection;
